<template>

  <v-container fluid class="px-0 receiver-order-info-messages">

    <!----------------------------------------------->
    <!-- Messages to Shipper (OrderShipperMessage) -->
    <!----------------------------------------------->
    <v-container fluid class="pl-10 pb-8 page-content">
      <v-row>
        <v-col cols="12">
          <div class="title">
            <v-icon style="font-size:18px;" class="pr-2">mdi-message-text-outline</v-icon>
            Messages to {{ shipperLocationName }}
          </div>
        </v-col>
      </v-row>
      <v-row class="mt-5" v-if="allowSendMessages">
        <v-col cols="12" xs="12" sm="12" md="5" lg="3" class="d-flex align-center">
          <v-btn
              outlined
              color="amber darken-2"
              @click="isMessageDialogOpen = true">
            Send Message to Shipper
          </v-btn>
          <v-tooltip right>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                  color="grey lighten-1"
                  style="font-size:20px;cursor:pointer;margin-left:8px"
                  v-bind="attrs"
                  v-on="on">
                mdi-information-outline
              </v-icon>
            </template>
            <div>Communicate with the shipper on issues or change requests for this order.</div>
          </v-tooltip>
        </v-col>
      </v-row>

      <v-container fluid class="pb-0" v-if="receiverMessageItems.length > 0">
        <!-- Message List Header -->
        <v-row class="mt-4 pb-1 message-list-header">
          <v-col cols="12" xs="12" sm="4" md="3" lg="3">
            Sent
          </v-col>
          <v-col cols="12" xs="12" sm="5" md="5" lg="6">
            Message
          </v-col>
          <v-col cols="12" xs="12" sm="3" md="4" lg="3" class="d-flex align-center justify-center">
            <span>Viewed by Shipper</span>
          </v-col>
        </v-row>

        <!-- Message List Content -->
        <v-row class="mb-6 message-item-container" :class="{ 'not-seen': !item.is_confirmed }" v-for="(item, index) in receiverMessageItems" :key="`receiver-message-${index}`">
          <v-col cols="12" xs="12" sm="4" md="3" lg="3" class="d-flex align-center">
            <div :class="item.is_confirmed ? 'sent-date-seen' : 'sent-date'">{{ formatDateTime(item.created_at) }}</div>
          </v-col>
          <v-col cols="12" xs="12" sm="5" md="5" lg="6" class="d-flex align-center">
            <div :class="item.is_confirmed ? 'message-text-seen' : 'message-text'">{{ item.message }}</div>
          </v-col>
          <v-col cols="12" xs="12" sm="3" md="4" lg="3" class="d-flex align-center justify-center">
            <div class="confirmed-date d-flex align-center" v-if="item.is_confirmed">
              <v-icon style="font-size:18px;color:#727272;" class="pr-2">mdi-eye-outline</v-icon>
              {{ formatDateTime(item.confirmed_at) }}
            </div>
            <template v-else-if="canEditMessages">
              <v-btn
                  icon
                  color="primary"
                  class="mr-8"
                  @click="editMessage(item)">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <dialog-confirm-button
                  buttonStyle="icon"
                  message="Delete this unseen message?"
                  icon="mdi-trash-can-outline"
                  iconColor="red accent-2"
                  v-on:confirmed="deleteMessage(item)" />
            </template>
          </v-col>
        </v-row>
      </v-container>

      <v-row v-else-if="!allowSendMessages">
        <v-col>
          <div class="no-messages-text">(No messages)</div>
        </v-col>
      </v-row>

    </v-container>

    <!------------------------->
    <!-- Send Message Dialog -->
    <!------------------------->
    <v-dialog persistent width="660" v-model="isMessageDialogOpen">
      <v-card>
        <!-- Title -->
        <v-card-title class="text-h5">
          Send Message to Shipper
        </v-card-title>

        <v-card-text class="mt-3 px-6 pb-2">
          <div class="d-flex align-center dialog-text">
            <div class="dialog-field-label">Order:</div>
            <div class="dialog-order-number">{{ order.order_number }}</div>
          </div>
          <div class="mt-3 d-flex align-center dialog-text">
            <div class="dialog-field-label">Shipper:</div>
            <div class="dialog-shipper-name">{{ order.shipper.name }}</div>
          </div>
          <div class="mt-3 d-flex align-center dialog-text">
            <div class="dialog-field-label">Shipping to:</div>
            <div class="dialog-receiver-name">{{ order.receiver.name }}</div>
          </div>
          <div class="mt-8">
            <v-textarea
                rows="5"
                outlined
                v-model.trim="message"
                :counter="maxChars"
                :rules="[v => !v || v.length <= maxChars || `Maximum ${maxChars} characters allowed`]"
                persistent-hint />
          </div>
        </v-card-text>

        <!-- Actions -->
        <v-card-actions class="mt-0">
          <v-btn
              large
              plain
              color="primary"
              class="ml-2 px-5"
              @click="close">
            Cancel
          </v-btn>
          <v-spacer />
          <v-btn
              v-if="isEditing"
              large
              color="primary"
              class="mb-2 mr-2 px-5"
              :disabled="!message"
              @click="updateMessage">
            Update Message
          </v-btn>
          <v-btn
              v-else
              large
              color="primary"
              class="mb-2 mr-2 px-5"
              :disabled="!message"
              @click="sendMessage">
            Send Message
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>

</template>

<script>
import DialogConfirmButton from '@/components/app/DialogConfirmButton'

export default {
  name: 'ReceiverOrderInfoMessages',

  components: {
    DialogConfirmButton
  },

  props: {
    orderId: {
      type: String,
      default: null
    }
  },

  data () {
    return {
      // loaded messages
      isDataLoading: false,
      dataLoadingError: null,
      receiverMessageData: null,
      // ---------- send message
      maxChars: 400,
      isMessageDialogOpen: false,
      message: null,
      isSending: false,
      sendError: null,
      // ---------- edit message
      isEditing: false,
      editingMessage: null,
      // ---------- delete message
      isDeleting: false,
      deleteError: null
    }
  },

  computed: {
    order () {
      return this.$parent.order
    },
    shipperLocationName () {
      return this.order?.shipper?.name || 'Shipper'
    },
    receiverLocationName () {
      return this.order?.receiver?.name || 'Receiver'
    },
    receiverMessageItems () {
      return (this.receiverMessageData) ? this.receiverMessageData.items : []
    },
    canEditMessages () {
      return this.order.order_status === 'PLACED' || this.order.order_status === 'BEING_SCHEDULED'
    },
    allowSendMessages () {
      return this.order && (this.order.order_status === 'PLACED' || this.order.order_status === 'BEING_SCHEDULED')
    }
  },

  async beforeMount () {
    await this.loadMessages()
  },

  methods: {
    // Display a date and time
    formatDateTime (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'MMM D, YYYY - h:mma') : ''
    },

    reset () {
      this.message = null
      this.sendError = null
      this.deleteError = null
    },

    close () {
      this.reset()
      this.isMessageDialogOpen = false
      this.isEditing = false
      this.editingMessage = null
    },

    // getMessagePreview (messageText) {
    //   const preview = (text, maxChars) => {
    //     if (text.length <= maxChars) return text

    //     // Find the last space within maxChars limit
    //     const lastSpace = text.substring(0, maxChars).lastIndexOf(' ')

    //     // If no space found, just cut at maxChars
    //     if (lastSpace === -1) return text.substring(0, maxChars) + '...'

    //     // Cut at the last space and add ellipsis
    //     return text.substring(0, lastSpace) + '...'
    //   }

    //   return preview(messageText, 80) // Adjust maxChars value as needed
    // },

    // Open message dialag to edit a message
    editMessage (item = {}) {
      this.isEditing = true
      this.editingMessage = item
      this.message = item.message
      this.isMessageDialogOpen = true
    },

    // Send message to shipper
    async sendMessage () {
      if (!this.message) return false
      this.isSending = true

      try {
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `order/${this.orderId}/shipper-message`,
          opts: {
            bodyData: {
              message: this.message
            }
          }
        })

        // Reload data
        this.loadMessages()
        this.close()
      } catch (err) {
        this.sendError = err.message || err.text || err
      } finally {
        this.isSending = false
      }
    },

    // Update message to driver
    async updateMessage () {
      if (!this.message || !this.editingMessage) return false
      this.isSending = true

      try {
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `order-shipper-message/${this.editingMessage.id}`,
          opts: {
            bodyData: {
              message: this.message
            }
          }
        })

        // Reload data
        this.loadMessages()
        this.close()
      } catch (err) {
        this.sendError = err.message || err.text || err
      } finally {
        this.isSending = false
      }
    },

    // Delete message
    async deleteMessage (item = {}) {
      if (!item.id) return false
      this.isDeleting = true

      try {
        await this.$store.dispatch('sh/dataApi', {
          method: 'delete',
          uri: `order-shipper-message/${item.id}`
        })

        // Reload data
        this.loadMessages()
      } catch (err) {
        this.deleteError = err.message || err.text || err
      } finally {
        this.isDeleting = false
      }
    },

    async loadMessages () {
      this.isDataLoading = true

      try {
        this.receiverMessageData = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: `order/${this.orderId}/shipper-messages`,
          opts: {
            params: {
            }
          }
        })
      } catch (err) {
        this.dataLoadingError = err.message || err.text || err
      } finally {
        this.isDataLoading = false
      }
    }
  }
}
</script>

<style scoped>
  .receiver-order-info-messages .info-text {
    color: #9c9c9c;
    font-size: 1.08rem;
    font-style: italic;
  }

  .receiver-order-info-messages .message-list-header {
    color: #9c9c9c;
    max-width: 900px;
  }

  .receiver-order-info-messages .message-item-container {
    border: 1px solid #525252;
    border-radius: 4px;
    max-width: 900px;
  }

  .receiver-order-info-messages .message-item-container.not-seen {
    border: 1px solid #a2a2a2;
  }

  .receiver-order-info-messages .sent-date {
    color: #b2b2b2;
    font-size: 0.9rem;
  }

  .receiver-order-info-messages .sent-date-seen {
    color: #9c9c9c;
    font-size: 0.9rem;
  }

  .receiver-order-info-messages .message-text-seen {
    color: #9c9c9c;
  }

  .receiver-order-info-messages .not-seen-text {
    color: #727272;
    font-style: italic;
  }

  .receiver-order-info-messages .no-messages-text {
    color: #9c9c9c;
  }

  .receiver-order-info-messages .confirmed-date {
    color: #727272;
    font-size: 0.9rem;
  }

  /* Dialog style */
  .dialog-text {
    font-size: 1.0rem;
  }
  .dialog-field-label {
    width: 110px;
    color: #9c9c9c;
  }
  .dialog-order-number {
    font-size: 1.08rem;
    color: #f7a725;
  }
  .dialog-shipper-name {
    color: #f2f2f2;
  }
  .dialog-receiver-name {
    color: #f2f2f2;
  }

  a.item-link {
    text-decoration: none;
  }

  a.item-link:hover {
    text-decoration: underline;
  }
</style>
